import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { useNavigate, Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, CheckCircle2, ChevronDown, ChevronUp, ThumbsUp, ShieldCheck, Star, ChevronLeft, ChevronRight, Zap, Layout, Brain, ShoppingBag, RefreshCw, Settings } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useDataStore } from '../store/dataStore';
const refMh = '/images/ref-mh.webp';
const refLld = '/images/ref-lld.webp';
const refCorex = '/images/ref-corex.webp';
const refAktueryal = '/images/ref-aktueryal.jpg';
const refEngb = '/images/engb-logo-bordo.svg';
const refDab = '/images/dabhukuk-logo.png';
const refGunfer = '/images/gunfer-gunaydin-logo.svg';
const heroImage = '/images/patoyazılıminfografik (4).svg';
const iconWeb = '/images/cross-platform.webp';
const iconAi = '/images/artificial-intelligence.webp';
const iconEcommerce = '/images/online-shop.webp';
const iconTransformation = '/images/digital-transformation.webp';
const iconCustomer = '/images/customer-experience.webp';
const iconProcess = '/images/transformation-3.webp';

import SEO from '../components/SEO';
import FadeIn from '../components/FadeIn';
import { useTranslation } from 'react-i18next';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { homePricingCards, faqs, serviceCategories } = useDataStore();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [lottieData, setLottieData] = useState<any>(null);

  useEffect(() => {
    fetch('https://assets3.lottiefiles.com/packages/lf20_ghs9bkkc.json')
      .then(response => response.json())
      .then(data => setLottieData(data))
      .catch(error => console.error('Error loading Lottie animation:', error));
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= 300;
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += 300;
    }
  };

  const REFERENCES = [
    { id: 1, name: 'MH Dizayn', image: refMh },
    { id: 2, name: 'LLD Hukuk', image: refLld },
    { id: 3, name: 'Corex Bomonti', image: refCorex },
    { id: 4, name: 'Aktüeryal', image: refAktueryal },
    { id: 5, name: 'ENGB Hukuk', image: refEngb },
    { id: 6, name: 'DAB Hukuk', image: refDab },
    { id: 7, name: 'Günfer Günaydın', image: refGunfer },
  ];

  return (
    <div className="w-full overflow-hidden">
      <SEO
        pageCode="home"
        title="Web Tasarım & Dijital Dönüşüm Ajansı"
        description="Şişli merkezli global dijital ajans Pato Yazılım. İşletmenize özel web tasarım, mobil uygulama, SEO ve marka danışmanlığı hizmetleri sunuyoruz."
        link="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 bg-brand-dark overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-brand-purple/20 to-transparent skew-x-12 transform origin-top-right"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-brand-blue/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <FadeIn className="space-y-6" direction="left">
            <div className="inline-flex items-center gap-2 px-6 py-2.5 bg-white rounded-full text-brand-blue font-bold text-sm shadow-lg shadow-brand-blue/20 animate-fade-in-up">
              <span>🚀</span>
              <span>{t('home.heroTag')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              {t('home.heroTitle').split('\n')[0]} <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-purple-400">{t('home.heroTitle').split('\n')[1].trim()}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-lg leading-relaxed pt-6">
              {t('home.heroDesc')}
            </p>

            <div className="flex flex-col sm:flex-row gap-5 pt-8">
              <button
                onClick={() => navigate(i18n.language === 'en' ? '/en/contact' : '/iletisim')}
                className="group bg-white text-brand-blue px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 hover:scale-105 hover:shadow-xl hover:shadow-brand-blue/20 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto min-w-[200px]"
              >
                {t('home.startNow')} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate(i18n.language === 'en' ? '/en/projects' : '/projelerimiz')}
                className="px-8 py-4 rounded-xl font-bold text-lg bg-[#353589] text-white hover:bg-[#2a2a6b] hover:scale-105 hover:shadow-xl hover:shadow-black/20 transition-all duration-300 w-full sm:w-auto min-w-[200px]"
              >
                {t('home.viewReferences')}
              </button>
            </div>
          </FadeIn>
          <FadeIn className="relative hidden lg:block" direction="right" delay={0.2}>
            <div className="relative z-10 bg-gradient-to-tr from-white/10 to-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-md transform rotate-3 hover:rotate-0 transition-all duration-500 shadow-2xl">
              <img
                src={heroImage}
                alt="Pato Yazılım Teknik Ekibi"
                className="rounded-lg shadow-lg w-full h-auto object-contain aspect-[4/3]"
                width="800"
                height="600"
                fetchPriority="high"
                loading="eager"
              />
              <div className="absolute -bottom-10 -left-10 bg-brand-dark p-6 rounded-xl border border-white/10 shadow-xl flex items-center gap-4 animate-bounce-slow">
                <div className="bg-brand-blue/20 p-3 rounded-full text-brand-blue">
                  <CheckCircle2 size={32} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{i18n.language === 'en' ? '100%' : '%100'}</div>
                  <div className="text-gray-400 text-sm">{t('home.customerSatisfaction')}</div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-brand-light">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-brand-blue font-bold tracking-wider mb-3">{t('common.ourServices').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</h2>
            <h3 className="text-4xl font-bold text-brand-dark mb-6">{t('home.servicesTitle')}</h3>
            <p className="text-gray-700 text-lg">
              {t('home.servicesDesc')}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCategories.filter(s => s.language === (i18n.language === 'en' ? 'EN' : 'TR')).map((service, idx) => {
              const Icon = (LucideIcons as any)[service.icon] || LucideIcons.Layers;
              return (
              <FadeIn key={service.id} delay={idx * 0.1}>
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group h-full">
                  <div className="w-16 h-16 bg-brand-blue/5 rounded-xl flex items-center justify-center text-brand-blue mb-6 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                    <Icon size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-brand-dark mb-3">{service.title}</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {service.description}
                  </p>
                </div>
              </FadeIn>
            )})}
          </div>
        </div>
      </section>

      {/* Innovation / Lottie Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <FadeIn className="w-full lg:w-1/2 order-2 lg:order-1" direction="left">
              <div className="relative min-h-[300px] md:min-h-[400px] lg:min-h-[500px] flex items-center justify-center">
                <div className="absolute -top-10 -left-10 w-32 h-32 bg-brand-blue/10 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-brand-purple/10 rounded-full blur-2xl"></div>
                {lottieData ? (
                  <Lottie
                    animationData={lottieData}
                    loop={true}
                    className="w-full max-w-lg mx-auto"
                  />
                ) : (
                  <div className="w-full max-w-lg mx-auto aspect-square rounded-full bg-gray-50 animate-pulse"></div>
                )}
              </div>
            </FadeIn>
            <FadeIn className="w-full lg:w-1/2 order-1 lg:order-2" direction="right" delay={0.2}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue/10 rounded-full text-brand-blue font-bold text-sm mb-6">
                <Zap size={16} />
                <span>{t('home.innovationTag')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
                {t('home.innovationTitle').split('\n')[0]} <br />
                <span className="text-brand-blue">{t('home.innovationTitle').split('\n')[1]?.trim()}</span>
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                {t('home.innovationDesc')}
              </p>
              <ul className="space-y-4">
                {(t('home.innovationPoints', { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-700 font-medium">
                    <div className="w-6 h-6 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                      <CheckCircle2 size={14} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Why Us / Features */}
      <section className="py-24 bg-[#353589] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <FadeIn className="mb-16 text-center">
            <h2 className="text-brand-blue font-bold tracking-wider mb-3">{t('common.whyUs').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</h2>
            <h3 className="text-4xl font-bold text-white mb-6">{t('home.whyUsTitle')}</h3>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              {t('home.whyUsDesc')}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Stratejik Web ve Mobil Geliştirme', desc: 'Hedef kitlenize ulaşan, kullanıcı dostu ve performans odaklı web siteleri ile mobil uygulamalar geliştirerek dijital varlığınızı güçlendiriyoruz. Her proje, markanızın hikayesini anlatacak şekilde özel olarak tasarlanır ve geliştirilir.', icon: iconWeb },
              { title: 'Veri Destekli Yapay Zeka ve Otomasyon', desc: 'İş süreçlerinizi optimize eden, verimliliği artıran ve karar alma süreçlerinizi destekleyen yapay zeka ve otomasyon çözümleri sunuyoruz. Büyük veriyi anlamlı içgörülere dönüştürerek rekabet avantajı elde etmenizi sağlıyoruz.', icon: iconAi },
              { title: 'E-ticaret ve Dijital Pazarlama', desc: 'Online satışlarınızı artırmak ve müşteri deneyimini iyileştirmek için güçlü e-ticaret platformları kuruyoruz. Dijital pazarlama stratejileriyle entegre çalışarak markanızın görünürlüğünü ve satışlarını en üst düzeye çıkarıyoruz.', icon: iconEcommerce },
              { title: 'Sürdürülebilir Dijital Dönüşüm Danışmanlığı', desc: 'İşletmenizin dijitalleşme yolculuğunda size rehberlik ediyor, teknolojik yenilikleri iş stratejilerinizle uyumlu hale getiriyoruz. Uzun vadeli başarı için sürdürülebilir ve adapte olabilir dijital çözümler tasarlıyoruz.', icon: iconTransformation },
              { title: 'Müşteri Güveni ve Marka Değeri Yükselten Deneyimler', desc: 'Dijital platformlarınızda şeffaflık, güvenlik ve kişiselleştirilmiş hizmet anlayışıyla müşteri güvenini pekiştiriyoruz. Pozitif kullanıcı deneyimleri, hızlı destek ve güvenilir altyapılarla markanızın itibarını ve tercih edilebilirliğini en üst seviyeye çıkarıyoruz.', icon: iconCustomer },
              { title: 'Akıllı İş Süreçleri ve Dijital Etkileşim Yönetimi', desc: 'Operasyonlarınızı dijitalleştirerek ve otomatize ederek verimliliği artırıyoruz. Müşteri ilişkileri yönetimi sistemleri, otomatik bildirimler ve etkileşim platformları sayesinde, marka ve müşteri arasındaki iletişimi daha düzenli, kişiselleştirilmiş ve kesintisiz hale getiriyoruz.', icon: iconProcess }
            ].map((item, idx) => (
              <FadeIn key={idx} delay={idx * 0.1}>
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group text-center h-full">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                    <img
                      src={item.icon}
                      alt={item.title}
                      width="80"
                      height="80"
                      className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-[#353589] group-hover:text-[#3e73fe] transition-colors mb-3">{item.title}</h4>
                  <p className="text-gray-700 leading-relaxed text-sm">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* References (Slider) */}
      <section className="py-16 bg-white border-b border-gray-100">
        <FadeIn className="container mx-auto px-4 md:px-8 mb-12 text-center">
          <h2 className="text-4xl font-bold text-brand-dark mb-4">{t('common.trustedBrands')}</h2>
        </FadeIn>

        <FadeIn className="container mx-auto px-4 md:px-8 relative group" delay={0.2}>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={scrollLeft}
              className="p-3 rounded-full bg-gray-50 hover:bg-brand-blue hover:text-white text-gray-400 transition-all shadow-sm hover:shadow-md hidden md:flex"
              aria-label="Önceki"
            >
              <ChevronLeft size={24} />
            </button>

            <div
              ref={scrollRef}
              className="flex overflow-x-auto gap-12 items-center py-6 px-4 scrollbar-hide scroll-smooth max-w-5xl mx-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {REFERENCES.map((ref) => (
                <div key={ref.id} className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-500 opacity-60 hover:opacity-100 hover:scale-105 transform cursor-pointer">
                  <img src={ref.image} alt={ref.name} width="200" height="80" className="h-16 md:h-20 w-auto object-contain max-w-[200px]" />
                </div>
              ))}
              {/* Placeholder for future references to show scrolling capability if list is short */}
              {/* We can duplicate the list to demonstrate scrolling if there are few items */}
              {REFERENCES.map((ref) => (
                <div key={`${ref.id}-dup`} className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-500 opacity-60 hover:opacity-100 hover:scale-105 transform cursor-pointer">
                  <img src={ref.image} alt={ref.name} width="200" height="80" className="h-16 md:h-20 w-auto object-contain max-w-[200px]" />
                </div>
              ))}
            </div>

            <button
              onClick={scrollRight}
              className="p-3 rounded-full bg-gray-50 hover:bg-brand-blue hover:text-white text-gray-400 transition-all shadow-sm hover:shadow-md hidden md:flex"
              aria-label="Sonraki"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </FadeIn>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-brand-light">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-brand-dark mb-4">{t('common.pricing')}</h2>
            <p className="text-gray-700">{t('common.pricingDesc')}</p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
            {homePricingCards.map((card, idx) => {
              const isDark = card.isPopular;
              const accentColor = "text-brand-blue";
              const accentBorder = "border-brand-blue";

              return (
                <FadeIn
                  key={idx}
                  delay={idx * 0.15}
                  className={`relative rounded-2xl p-8 shadow-xl border flex flex-col h-full transition-all duration-300 ${isDark
                    ? `bg-brand-dark text-white ${accentBorder} transform scale-105 z-10`
                    : 'bg-white text-brand-dark border-gray-100'
                    }`}
                >

                  <div className="mb-8">
                    <div className="flex justify-start mb-4">
                      <div className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 ${isDark ? 'bg-white/10 text-white' : 'bg-brand-blue/10 text-brand-blue'
                        }`}>
                        {idx === 0 && <ThumbsUp size={14} />}
                        {idx === 1 && <ShieldCheck size={14} />}
                        {idx === 2 && <Star size={14} />}
                        Öne Çıkan
                      </div>
                    </div>

                    <h3 className="text-3xl font-bold mb-4">{card.title}</h3>

                    <p className={`text-sm leading-relaxed mb-6 min-h-[40px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {card.description}
                    </p>

                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-brand-dark'}`}>{card.price}</span>
                      {card.priceSuffix && (
                        <span className={`text-xs font-bold ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{card.priceSuffix}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-grow">
                    <ul className="space-y-4 mb-8">
                      {card.features.map((feature, fIdx) => (
                        <li key={fIdx} className={`flex items-start gap-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <div className="mt-0.5 min-w-[20px]">
                            <CheckCircle2 size={20} className={accentColor} />
                          </div>
                          <span className="leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {card.footnote && (
                    <div className={`mt-auto mb-6 text-[11px] leading-tight italic opacity-80 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      {card.footnote}
                    </div>
                  )}

                  <div className={card.footnote ? "" : "mt-auto"}>
                    <Link
                      to={card.linkTo || '/hizmetlerimiz'}
                      className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 transition-all font-bold ${isDark
                        ? 'bg-white text-brand-dark hover:bg-brand-blue hover:text-white shadow-lg'
                        : 'bg-brand-dark text-white hover:bg-brand-blue'
                        }`}
                    >
                      Detaylar
                    </Link>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <FadeIn className="text-center mb-16">
            <h2 className="text-3xl font-bold text-brand-dark mb-4">{t('common.faq')}</h2>
            <p className="text-gray-700">{t('common.faqDesc')}</p>
          </FadeIn>
          <div className="space-y-4">
            {faqs.slice(0, 4).map((faq, idx) => (
              <FadeIn key={idx} delay={idx * 0.1}>
                <div className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === idx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
                  <button
                    onClick={() => toggleFaq(idx)}
                    className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === idx ? 'text-white' : 'text-brand-dark'}`}
                  >
                    <span className="font-medium text-lg">{faq.question}</span>
                    {openFaq === idx ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  <div className={`transition-all duration-300 ease-in-out ${openFaq === idx ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="p-6 pt-0 text-white">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-12 text-center" delay={0.4}>
            <button
              onClick={() => navigate(i18n.language === 'en' ? '/en/faq' : '/sss')}
              className="inline-flex items-center gap-2 text-brand-blue border border-brand-blue/20 px-8 py-3 rounded-full hover:bg-brand-blue/5 transition-all font-medium"
            >
              {t('common.seeMore')} <ArrowRight size={18} />
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Home;
